import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, fmtCurrency, fmtPct } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import PerformanceChart from "@/components/PerformanceChart";
import { ArrowLeft, Plus, Trash2, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HOLDING_TYPES = ["Stocks", "ETF", "Mutual Funds", "Bonds", "Crypto", "Cash", "Real Estate"];
const TXN_TYPES = ["Deposit", "Withdrawal", "Buy", "Sell", "Dividend"];
const DOC_TYPES = ["Statement", "Tax Document", "Report"];

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-client", id],
    queryFn: async () => (await api.get(`/admin/clients/${id}`)).data,
  });

  const [holdings, setHoldings] = useState([]);
  const [perf, setPerf] = useState([]);
  const [txn, setTxn] = useState({ date: "", type: "Deposit", description: "", amount: "" });
  const [doc, setDoc] = useState({ name: "", type: "Statement", date: "" });

  useEffect(() => {
    if (data) {
      setHoldings((data.portfolio?.holdings || []).map((h) => ({ ...h, _key: crypto.randomUUID() })));
      setPerf((data.portfolio?.performance || []).map((p) => ({ ...p, _key: crypto.randomUUID() })));
    }
  }, [data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-client", id] });
    qc.invalidateQueries({ queryKey: ["admin-clients"] });
  };

  const savePortfolio = useMutation({
    mutationFn: async () => api.put(`/admin/clients/${id}/portfolio`, {
      holdings: holdings.map((h) => ({ name: h.name, type: h.type, invested: Number(h.invested) || 0, current_value: Number(h.current_value) || 0 })),
      performance: perf.map((p) => ({ date: p.date, value: Number(p.value) || 0 })),
    }),
    onSuccess: () => { toast.success("Portfolio saved"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const addTxn = useMutation({
    mutationFn: async () => api.post(`/admin/clients/${id}/transactions`, { ...txn, amount: Number(txn.amount) || 0 }),
    onSuccess: () => { toast.success("Transaction added"); setTxn({ date: "", type: "Deposit", description: "", amount: "" }); invalidate(); },
  });
  const delTxn = useMutation({
    mutationFn: async (tid) => api.delete(`/admin/clients/${id}/transactions/${tid}`),
    onSuccess: () => { toast.success("Removed"); invalidate(); },
  });
  const addDoc = useMutation({
    mutationFn: async () => api.post(`/admin/clients/${id}/documents`, doc),
    onSuccess: () => { toast.success("Document added"); setDoc({ name: "", type: "Statement", date: "" }); invalidate(); },
  });
  const delDoc = useMutation({
    mutationFn: async (did) => api.delete(`/admin/clients/${id}/documents/${did}`),
    onSuccess: () => { toast.success("Removed"); invalidate(); },
  });
  const delClient = useMutation({
    mutationFn: async () => api.delete(`/admin/clients/${id}`),
    onSuccess: () => { toast.success("Client deleted"); qc.invalidateQueries({ queryKey: ["admin-clients"] }); navigate("/admin"); },
  });

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 rounded-lg" /></div>;

  const s = data?.summary || data?.portfolio?.summary || {};
  const invested = holdings.reduce((a, h) => a + (Number(h.current_value) || 0) - 0, 0);
  const curVal = holdings.reduce((a, h) => a + (Number(h.current_value) || 0), 0);
  const inv = holdings.reduce((a, h) => a + (Number(h.invested) || 0), 0);
  const gain = curVal - inv;
  const gainPct = inv ? (gain / inv) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="admin-client-detail">
      <button onClick={() => navigate("/admin")} data-testid="back-btn" className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#15803d] transition-colors duration-200">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to clients
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-1">{data?.email}{data?.phone ? ` · ${data.phone}` : ""}</p>
          <h1 className="font-serif text-4xl text-[#0f172a]">{data?.name}</h1>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" data-testid="delete-client-btn" className="rounded-full border-red-200 text-[#dc2626] hover:bg-red-50 transition-colors duration-200">
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Delete client
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-2xl">Delete {data?.name}?</AlertDialogTitle>
              <AlertDialogDescription>This permanently removes the client account, portfolio, transactions and documents.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-delete-client" onClick={() => delClient.mutate()} className="bg-[#dc2626] hover:bg-red-700">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid gap-6 sm:grid-cols-3">
        <Card className="p-5 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
          <p className="overline mb-2">Current Value</p>
          <p className="text-2xl font-bold tabular-nums text-[#0f172a]">{fmtCurrency(curVal)}</p>
        </Card>
        <Card className="p-5 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
          <p className="overline mb-2">Invested</p>
          <p className="text-2xl font-bold tabular-nums text-[#0f172a]">{fmtCurrency(inv)}</p>
        </Card>
        <Card className="p-5 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
          <p className="overline mb-2">Gain / Loss</p>
          <p className={`text-2xl font-bold tabular-nums ${gain >= 0 ? "text-[#059669]" : "text-[#dc2626]"}`}>{fmtCurrency(gain)} <span className="text-base">({fmtPct(gainPct)})</span></p>
        </Card>
      </div>

      <Tabs defaultValue="holdings">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="holdings" data-testid="tab-holdings">Holdings & Performance</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        </TabsList>

        {/* HOLDINGS */}
        <TabsContent value="holdings" className="space-y-6 mt-4">
          <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl text-[#0f172a]">Holdings</h3>
              <Button variant="outline" size="sm" data-testid="add-holding-btn"
                onClick={() => setHoldings([...holdings, { name: "", type: "Stocks", invested: 0, current_value: 0, _key: crypto.randomUUID() }])}
                className="rounded-full border-slate-300 hover:bg-slate-50 transition-colors duration-200">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add holding
              </Button>
            </div>
            <div className="space-y-3">
              {holdings.length === 0 && <p className="text-slate-400 text-sm py-4">No holdings. Add one to build the portfolio.</p>}
              {holdings.map((h, i) => (
                <div key={h._key || i} data-testid={`holding-row-${i}`} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-4" placeholder="Asset name" value={h.name}
                    onChange={(e) => { const n = [...holdings]; n[i].name = e.target.value; setHoldings(n); }} />
                  <div className="col-span-3">
                    <Select value={h.type} onValueChange={(v) => { const n = [...holdings]; n[i].type = v; setHoldings(n); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{HOLDING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2 text-right tabular-nums" type="number" placeholder="Invested" value={h.invested}
                    onChange={(e) => { const n = [...holdings]; n[i].invested = e.target.value; setHoldings(n); }} />
                  <Input className="col-span-2 text-right tabular-nums" type="number" placeholder="Value" value={h.current_value}
                    onChange={(e) => { const n = [...holdings]; n[i].current_value = e.target.value; setHoldings(n); }} />
                  <Button variant="ghost" size="icon" data-testid={`remove-holding-${i}`}
                    onClick={() => setHoldings(holdings.filter((_, x) => x !== i))}
                    className="col-span-1 text-slate-400 hover:text-[#dc2626] hover:bg-red-50">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-serif text-2xl text-[#0f172a]">Performance History</h3>
                <p className="text-sm text-slate-400">Monthly value points shown on the client chart.</p>
              </div>
              <Button variant="outline" size="sm" data-testid="add-perf-btn"
                onClick={() => setPerf([...perf, { date: "", value: 0, _key: crypto.randomUUID() }])}
                className="rounded-full border-slate-300 hover:bg-slate-50 transition-colors duration-200">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add point
              </Button>
            </div>
            {perf.length > 0 && <div className="mb-4"><PerformanceChart data={perf.map((p) => ({ date: p.date, value: Number(p.value) || 0 }))} /></div>}
            <div className="grid sm:grid-cols-2 gap-2">
              {perf.map((p, i) => (
                <div key={p._key || i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-6" type="month" value={p.date}
                    onChange={(e) => { const n = [...perf]; n[i].date = e.target.value; setPerf(n); }} />
                  <Input className="col-span-5 text-right tabular-nums" type="number" placeholder="Value" value={p.value}
                    onChange={(e) => { const n = [...perf]; n[i].value = e.target.value; setPerf(n); }} />
                  <Button variant="ghost" size="icon" onClick={() => setPerf(perf.filter((_, x) => x !== i))}
                    className="col-span-1 text-slate-400 hover:text-[#dc2626] hover:bg-red-50">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
            </div>
          </Card>

          <Button data-testid="save-portfolio-btn" disabled={savePortfolio.isPending} onClick={() => savePortfolio.mutate()}
            className="rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200">
            {savePortfolio.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save portfolio</>}
          </Button>
        </TabsContent>

        {/* TRANSACTIONS */}
        <TabsContent value="transactions" className="space-y-4 mt-4">
          <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
            <h3 className="font-serif text-2xl text-[#0f172a] mb-4">Add transaction</h3>
            <div className="grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-3 space-y-1.5"><Label>Date</Label>
                <Input type="date" data-testid="txn-date" value={txn.date} onChange={(e) => setTxn({ ...txn, date: e.target.value })} /></div>
              <div className="sm:col-span-2 space-y-1.5"><Label>Type</Label>
                <Select value={txn.type} onValueChange={(v) => setTxn({ ...txn, type: v })}>
                  <SelectTrigger data-testid="txn-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TXN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-4 space-y-1.5"><Label>Description</Label>
                <Input data-testid="txn-desc" value={txn.description} onChange={(e) => setTxn({ ...txn, description: e.target.value })} /></div>
              <div className="sm:col-span-2 space-y-1.5"><Label>Amount</Label>
                <Input type="number" data-testid="txn-amount" value={txn.amount} onChange={(e) => setTxn({ ...txn, amount: e.target.value })} /></div>
              <Button className="sm:col-span-1 rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200"
                data-testid="add-txn-btn" disabled={!txn.date || addTxn.isPending} onClick={() => addTxn.mutate()}>
                <Plus className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </Card>
          <Card className="bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-[#f8f9fa] hover:bg-[#f8f9fa]">
                <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(data?.transactions || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No transactions</TableCell></TableRow>}
                {(data?.transactions || []).map((t) => (
                  <TableRow key={t.id} className="hover:bg-slate-50 transition-colors duration-200">
                    <TableCell className="tabular-nums text-slate-600">{t.date}</TableCell>
                    <TableCell>{t.type}</TableCell>
                    <TableCell className="text-slate-700">{t.description}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{fmtCurrency(t.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => delTxn.mutate(t.id)} className="text-slate-400 hover:text-[#dc2626] hover:bg-red-50">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="space-y-4 mt-4">
          <Card className="p-6 bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)]">
            <h3 className="font-serif text-2xl text-[#0f172a] mb-4">Add document</h3>
            <div className="grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-5 space-y-1.5"><Label>Name</Label>
                <Input data-testid="doc-name" value={doc.name} onChange={(e) => setDoc({ ...doc, name: e.target.value })} /></div>
              <div className="sm:col-span-3 space-y-1.5"><Label>Type</Label>
                <Select value={doc.type} onValueChange={(v) => setDoc({ ...doc, type: v })}>
                  <SelectTrigger data-testid="doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-3 space-y-1.5"><Label>Date</Label>
                <Input type="date" data-testid="doc-date" value={doc.date} onChange={(e) => setDoc({ ...doc, date: e.target.value })} /></div>
              <Button className="sm:col-span-1 rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200"
                data-testid="add-doc-btn" disabled={!doc.name || !doc.date || addDoc.isPending} onClick={() => addDoc.mutate()}>
                <Plus className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </Card>
          <div className="grid gap-3">
            {(data?.documents || []).length === 0 && <p className="text-slate-400 text-sm">No documents</p>}
            {(data?.documents || []).map((d) => (
              <Card key={d.id} className="p-4 bg-white border-slate-200 flex items-center justify-between">
                <div><p className="font-medium text-slate-800">{d.name}</p><p className="text-sm text-slate-400">{d.type} · {d.date}</p></div>
                <Button variant="ghost" size="icon" onClick={() => delDoc.mutate(d.id)} className="text-slate-400 hover:text-[#dc2626] hover:bg-red-50">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} /></Button>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
